import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { GroupDto, CreateGroupDto, ResponseDto } from "@mimber/schema"
import { groupsKeys } from "../groups.utils"

interface CreateGroupOptions extends Omit<
  UseMutationOptions<ResponseDto<GroupDto>, Error, CreateGroupDto>,
  "mutationFn"
> {}

/**
 * Hook for creating a new group
 *
 * @param options - Additional options for the mutation
 * @returns Mutation hook for creating a group
 *
 * @example
 * ```tsx
 * const createGroup = useCreateGroup({
 *   onSuccess: () => {
 *     toast.success('Group created successfully!')
 *   }
 * })
 *
 * createGroup.mutate({
 *   name: "Engineering Team",
 *   type: "COMMITTEE",
 *   visibility: "PUBLIC"
 * })
 * ```
 */
export const useCreateGroup = (
  options: CreateGroupOptions = {}
): UseMutationResult<ResponseDto<GroupDto>, Error, CreateGroupDto> => {
  guardAppUsage("mimber-organization", "useCreateGroup")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: CreateGroupDto) => {
      const response = await server.post<ResponseDto<GroupDto>>("groups", data)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate groups list to refetch with new group
      queryClient.invalidateQueries({
        queryKey: groupsKeys.findAll(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
